class EuWithdrawalForm extends HTMLElement {
  static turnstileScriptPromise = null;

  connectedCallback() {
    if (this.listenersAttached) return;

    this.form = this.querySelector('form');
    this.reviewPanel = this.querySelector('[data-eu-withdrawal-review]');
    this.successPanel = this.querySelector('[data-eu-withdrawal-success]');
    this.errorPanel = this.querySelector('[data-eu-withdrawal-error]');
    this.reviewButton = this.querySelector('[data-eu-withdrawal-review-button]');
    this.backButton = this.querySelector('[data-eu-withdrawal-back-button]');
    this.submitButton = this.querySelector('[data-eu-withdrawal-submit-button]');
    this.scopeInputs = this.querySelectorAll('input[name="withdrawalScope"]');
    this.itemDetailsField = this.querySelector('[name="withdrawalItemDetails"]');
    this.itemDetailsGroup = this.querySelector('[data-eu-withdrawal-item-details]');
    this.turnstileContainer = this.querySelector('[data-eu-withdrawal-turnstile]');
    this.turnstileToken = '';
    this.turnstileWidgetId = null;

    if (!this.form) return;

    this.handleReviewClick = () => this.showReview();
    this.handleBackClick = () => this.showForm();
    this.handleSubmit = (event) => this.submit(event);
    this.handleScopeChange = () => this.updateScopeState();

    this.reviewButton?.addEventListener('click', this.handleReviewClick);
    this.backButton?.addEventListener('click', this.handleBackClick);
    this.form.addEventListener('submit', this.handleSubmit);
    this.scopeInputs.forEach((input) => input.addEventListener('change', this.handleScopeChange));

    this.listenersAttached = true;
    this.updateScopeState();
  }

  disconnectedCallback() {
    this.reviewButton?.removeEventListener('click', this.handleReviewClick);
    this.backButton?.removeEventListener('click', this.handleBackClick);
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.scopeInputs?.forEach((input) => input.removeEventListener('change', this.handleScopeChange));
    this.listenersAttached = false;
  }

  async prepareTurnstile() {
    if (this.turnstileWidgetId !== null || !this.turnstileContainer || !this.dataset.turnstileSiteKey) return;

    await EuWithdrawalForm.loadTurnstileScript();
    this.renderTurnstile();
  }

  static loadTurnstileScript() {
    if (window.turnstile?.render) return Promise.resolve();

    if (EuWithdrawalForm.turnstileScriptPromise) return EuWithdrawalForm.turnstileScriptPromise;

    EuWithdrawalForm.turnstileScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');

      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener('error', () => reject(new Error('Turnstile could not load.')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => reject(new Error('Turnstile could not load.')), { once: true });
      document.head.appendChild(script);
    });

    return EuWithdrawalForm.turnstileScriptPromise;
  }

  getSelectedScope() {
    return this.querySelector('input[name="withdrawalScope"]:checked')?.value || 'full';
  }

  updateScopeState() {
    const isPartial = this.getSelectedScope() === 'partial';

    if (this.itemDetailsGroup) {
      this.itemDetailsGroup.hidden = !isPartial;
    }

    if (this.itemDetailsField) {
      this.itemDetailsField.required = isPartial;
      this.itemDetailsField.setAttribute('aria-required', String(isPartial));
      if (!isPartial) this.itemDetailsField.value = '';
    }
  }

  validateFields() {
    this.updateScopeState();

    const requiredFields = ['withdrawalName', 'withdrawalEmail', 'withdrawalOrderIdentifier'];
    const invalidField = requiredFields
      .map((name) => this.form.elements[name])
      .find((field) => !field?.value.trim());

    if (invalidField) {
      invalidField.focus();
      this.showError('Please complete the required fields.');
      return false;
    }

    const emailField = this.form.elements.withdrawalEmail;

    if (emailField instanceof HTMLInputElement && !emailField.validity.valid) {
      emailField.focus();
      this.showError('Please enter a valid confirmation email.');
      return false;
    }

    if (this.getSelectedScope() === 'partial' && !this.itemDetailsField?.value.trim()) {
      this.itemDetailsField?.focus();
      this.showError('Please list the items included in the withdrawal request.');
      return false;
    }

    this.showError('');
    return true;
  }

  collectFields() {
    const formData = new FormData(this.form);

    return {
      name: String(formData.get('withdrawalName') || '').trim(),
      email: String(formData.get('withdrawalEmail') || '').trim(),
      orderIdentifier: String(formData.get('withdrawalOrderIdentifier') || '').trim(),
      withdrawalScope: this.getSelectedScope(),
      itemDetails: String(formData.get('withdrawalItemDetails') || '').trim(),
    };
  }

  showReview() {
    if (!this.validateFields()) return;

    const fields = this.collectFields();
    const scopeText = fields.withdrawalScope === 'partial' ? 'Part of the order' : 'Full order';
    const reviewValues = {
      name: fields.name,
      email: fields.email,
      orderIdentifier: fields.orderIdentifier,
      withdrawalScope: scopeText,
      itemDetails: fields.withdrawalScope === 'partial' ? fields.itemDetails : 'Not applicable',
    };

    Object.entries(reviewValues).forEach(([key, value]) => {
      const target = this.querySelector(`[data-eu-withdrawal-review-value="${key}"]`);
      if (target) target.textContent = value;
    });

    this.form.dataset.step = 'review';
    this.reviewPanel.hidden = false;
    this.submitButton?.focus();
  }

  showForm() {
    this.form.dataset.step = 'edit';
    this.reviewPanel.hidden = true;
    this.reviewButton?.focus();
  }

  async submit(event) {
    event.preventDefault();

    if (this.form.dataset.step !== 'review') {
      this.showReview();
      return;
    }

    if (!this.validateFields()) {
      this.showForm();
      return;
    }

    try {
      await this.prepareTurnstile();
    } catch (error) {
      this.showError(error.message || 'The security check could not load. Please try again.');
      return;
    }

    if (!this.turnstileToken) {
      this.showError('Please complete the security check.');
      return;
    }

    const endpoint = this.dataset.endpoint;

    if (!endpoint) {
      this.showError('This form is not ready yet. Please contact us for help.');
      return;
    }

    this.setSubmitting(true);

    try {
      const body = JSON.stringify({
        confirmation: true,
        turnstileToken: this.turnstileToken,
        honeypot: String(this.form.elements.honeypot?.value || ''),
        fields: this.collectFields(),
        context: {
          pageUrl: window.location.href,
          market: this.dataset.market || 'EU',
          source: 'shopify-retail-eu-withdrawal',
        },
      });

      if (new Blob([body]).size > 32768) {
        throw new Error('The withdrawal request is too long. Please shorten the item details and try again.');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.ok !== true) {
        throw new Error(result.message || 'The withdrawal request could not be submitted. Please check the form and try again.');
      }

      this.showSuccess(result);
    } catch (error) {
      this.showError(this.getSubmitErrorMessage(error));
      this.resetTurnstile();
    } finally {
      this.setSubmitting(false);
    }
  }

  getSubmitErrorMessage(error) {
    const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const isFetchFailure = error instanceof TypeError && /fetch/i.test(error.message || '');

    if (isLocalDev && isFetchFailure) {
      return 'Local development cannot submit this form because the submission server only accepts approved shop origins. Test from a Shopify preview, or temporarily allow the local dev origin in the submission server.';
    }

    return error.message || 'The withdrawal request could not be submitted. Please try again.';
  }

  showSuccess(result) {
    this.form.hidden = true;
    this.reviewPanel.hidden = true;
    this.showError('');

    const requestId = this.querySelector('[data-eu-withdrawal-success-id]');
    const submittedAt = this.querySelector('[data-eu-withdrawal-success-time]');

    if (requestId) requestId.textContent = result.submissionId || 'Received';
    if (submittedAt) submittedAt.textContent = result.submittedAt ? new Date(result.submittedAt).toLocaleString() : '';
    if (this.successPanel) {
      this.successPanel.hidden = false;
      this.successPanel.focus();
    }
  }

  showError(message) {
    if (!this.errorPanel) return;

    this.errorPanel.textContent = message;
    this.errorPanel.hidden = !message;
  }

  setSubmitting(isSubmitting) {
    if (this.submitButton) {
      this.submitButton.disabled = isSubmitting;
      this.submitButton.textContent = isSubmitting ? 'Submitting...' : 'Confirm withdrawal';
    }
  }

  renderTurnstile() {
    if (!this.turnstileContainer || !this.dataset.turnstileSiteKey || !window.turnstile?.render) return;

    this.turnstileWidgetId = window.turnstile.render(this.turnstileContainer, {
      sitekey: this.dataset.turnstileSiteKey,
      callback: (token) => {
        this.turnstileToken = token;
        this.showError('');
      },
      'expired-callback': () => {
        this.turnstileToken = '';
        this.showError('The security check expired. Please complete it again.');
      },
      'error-callback': () => {
        this.turnstileToken = '';
        this.showError('The security check could not be completed. Please try again.');
      },
    });
  }

  resetTurnstile() {
    this.turnstileToken = '';

    if (this.turnstileWidgetId !== null && window.turnstile?.reset) {
      window.turnstile.reset(this.turnstileWidgetId);
    }
  }
}

if (!customElements.get('eu-withdrawal-form')) {
  customElements.define('eu-withdrawal-form', EuWithdrawalForm);
}

class EuWithdrawalDialog extends HTMLElement {
  connectedCallback() {
    if (this.listenersAttached) return;

    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('eu-withdrawal-form');
    this.openButtonSelector = `[data-eu-withdrawal-open][aria-controls="${this.dialog?.id}"]`;

    if (!this.dialog) return;

    this.closeButtons = this.querySelectorAll('[data-eu-withdrawal-close]');
    this.handleCloseButtonClick = () => this.close();
    this.handleDialogClick = (event) => {
      if (event.target === this.dialog) this.close();
    };
    this.handleDialogClosed = () => {
      document.documentElement.removeAttribute('scroll-lock');
    };

    document.addEventListener('click', this.handleDocumentClick);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.handleCloseButtonClick));
    this.dialog.addEventListener('click', this.handleDialogClick);
    this.dialog.addEventListener('close', this.handleDialogClosed);
    this.dialog.addEventListener('cancel', this.handleDialogClosed);
    this.listenersAttached = true;
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleCloseButtonClick));
    this.dialog?.removeEventListener('click', this.handleDialogClick);
    this.dialog?.removeEventListener('close', this.handleDialogClosed);
    this.dialog?.removeEventListener('cancel', this.handleDialogClosed);
    this.listenersAttached = false;
  }

  handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(this.openButtonSelector);
    if (!trigger) return;

    event.preventDefault();
    this.open();
  };

  async open() {
    if (this.dialog.open) return;

    this.dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    try {
      await this.form?.prepareTurnstile();
    } catch (error) {
      this.form?.showError(error.message || 'The security check could not load. Please try again.');
    }
    this.querySelector('[data-eu-withdrawal-initial-focus]')?.focus();
  }

  close() {
    if (!this.dialog.open) return;

    this.dialog.close();
    document.documentElement.removeAttribute('scroll-lock');
  }
}

if (!customElements.get('eu-withdrawal-dialog')) {
  customElements.define('eu-withdrawal-dialog', EuWithdrawalDialog);
}
